import { useMutation } from "@apollo/client";
import {
  Box,
  Chip,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import {
  SUBMISSION_STATUS_EMOJI_LABEL,
  VALID_ENCAMP_STATUSES,
} from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionEncampStatus, SubmissionStatus } from "generated-graphql/graphql";
import { UPDATE_SUBMISSION_ENCAMP_STATUS_MUTATION } from "queries/submissions";
import { useCallback, useEffect, useState, type MouseEvent } from "react";

export function getStatusDisplay(status: SubmissionStatus | string): string {
  return SUBMISSION_STATUS_EMOJI_LABEL[status] ?? prettyPrintEnumValue(status);
}

export function getEncampStatusDisplay(status: SubmissionEncampStatus | string): string {
  switch (status) {
    case SubmissionEncampStatus.Blocked:
      return "Blocked";
    case SubmissionEncampStatus.NeedsCorrection:
      return "Needs Correction";
    case SubmissionEncampStatus.Rejected:
      return "Rejected";
    case SubmissionEncampStatus.Resolving:
      return "Resolving";
    case SubmissionEncampStatus.Processing:
      return "Filing in Progress";
    default:
      return prettyPrintEnumValue(status);
  }
}

function getStatusColor(status: SubmissionStatus | string): string {
  switch (status) {
    case SubmissionStatus.NotStarted:
      return "#f57c00";
    case SubmissionStatus.InProgress:
      return "#1976d2";
    case SubmissionStatus.EncampInitialReview:
      return "#9c27b0";
    case SubmissionStatus.InReview:
      return "#0288d1";
    case SubmissionStatus.ProcessingSubmission:
      return "#7b1fa2";
    case SubmissionStatus.FilingComplete:
      return "#388e3c";
    case SubmissionStatus.NotReporting:
      return "#757575";
    default:
      return "#757575";
  }
}

function getEncampStatusColor(status: SubmissionEncampStatus | string): string {
  switch (status) {
    case SubmissionEncampStatus.Blocked:
      return "#d32f2f";
    case SubmissionEncampStatus.NeedsCorrection:
      return "#e65100";
    case SubmissionEncampStatus.Rejected:
      return "#c62828";
    case SubmissionEncampStatus.Resolving:
      return "#f9a825";
    case SubmissionEncampStatus.Processing:
      return "#7b1fa2";
    default:
      return "#757575";
  }
}

interface SubmissionStatusChipWithPopoverProps {
  submissionId?: string | null;
  submissionStatus: SubmissionStatus | string;
  encampStatus?: SubmissionEncampStatus | null;
  isStaff: boolean;
  onUpdated?: () => void;
}

export function SubmissionStatusChipWithPopover({
  submissionId,
  submissionStatus,
  encampStatus,
  isStaff,
  onUpdated,
}: SubmissionStatusChipWithPopoverProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localEncampStatus, setLocalEncampStatus] = useState<
    SubmissionEncampStatus | null | undefined
  >(encampStatus);

  useEffect(() => {
    setLocalEncampStatus(encampStatus);
  }, [encampStatus]);

  const [updateEncampStatus, { loading }] = useMutation(UPDATE_SUBMISSION_ENCAMP_STATUS_MUTATION, {
    refetchQueries: ["WasteSubmissionsForStaff", "GetSubmission", "GetSubmissionStepper"],
  });

  const validSubStatuses = VALID_ENCAMP_STATUSES[submissionStatus] ?? [];
  const hasSubStatuses = validSubStatuses.length > 0;
  const activeEncampStatus = localEncampStatus ?? encampStatus;
  const isRejected = activeEncampStatus === SubmissionEncampStatus.Rejected;
  const clickable = isStaff && hasSubStatuses && !!submissionId && !isRejected;

  const handleChipClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!clickable) return;
      e.preventDefault();
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    },
    [clickable]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const saveEncampStatus = useCallback(
    (newStatus: SubmissionEncampStatus | null) => {
      if (!submissionId || loading) return;
      setLocalEncampStatus(newStatus);
      setAnchorEl(null);
      updateEncampStatus({
        variables: { submissionId, encampStatus: newStatus },
      })
        .then(() => onUpdated?.())
        .catch(() => setLocalEncampStatus(encampStatus));
    },
    [submissionId, loading, updateEncampStatus, onUpdated, encampStatus]
  );

  const handleToggle = useCallback(
    (_: MouseEvent<HTMLElement>, newValue: SubmissionEncampStatus | null) => {
      if (isRejected) return;
      const newStatus = newValue === localEncampStatus ? null : newValue;
      saveEncampStatus(newStatus);
    },
    [localEncampStatus, saveEncampStatus, isRejected]
  );

  const handleClear = useCallback(() => {
    if (isRejected) return;
    saveEncampStatus(null);
  }, [saveEncampStatus, isRejected]);
  const statusLabel = getStatusDisplay(submissionStatus);
  const chipLabel = activeEncampStatus
    ? `${statusLabel} - ${getEncampStatusDisplay(activeEncampStatus)}`
    : statusLabel;
  const chipColor = activeEncampStatus
    ? getEncampStatusColor(activeEncampStatus)
    : getStatusColor(submissionStatus);

  return (
    <>
      <Chip
        label={chipLabel}
        size="small"
        onClick={clickable ? handleChipClick : undefined}
        sx={{
          backgroundColor: alpha(chipColor, 0.15),
          color: chipColor,
          fontWeight: 500,
          cursor: clickable ? "pointer" : "default",
          "&:hover": clickable ? { backgroundColor: alpha(chipColor, 0.25) } : undefined,
        }}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { p: 2, minWidth: 200 } },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isRejected ? (
          <Typography variant="body2" color="warning.main" sx={{ maxWidth: 280 }}>
            This submission is <strong>Rejected</strong>. Use the rejection routing dialog to
            resolve it before changing the encamp status.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Set Encamp Status
              </Typography>
              {activeEncampStatus && (
                <Typography
                  variant="caption"
                  color="primary"
                  onClick={handleClear}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Clear
                </Typography>
              )}
            </Box>
            <ToggleButtonGroup
              orientation="vertical"
              exclusive
              value={activeEncampStatus ?? null}
              onChange={handleToggle}
              fullWidth
              size="small"
            >
              {validSubStatuses
                .filter((status) => status !== SubmissionEncampStatus.Rejected)
                .map((status) => (
                  <ToggleButton
                    key={status}
                    value={status}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      justifyContent: "flex-start",
                      px: 2,
                      py: 0.75,
                      "&.Mui-selected": {
                        backgroundColor: alpha(getEncampStatusColor(status), 0.15),
                        color: getEncampStatusColor(status),
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: alpha(getEncampStatusColor(status), 0.25),
                        },
                      },
                    }}
                  >
                    {getEncampStatusDisplay(status)}
                  </ToggleButton>
                ))}
            </ToggleButtonGroup>
          </>
        )}
      </Popover>
    </>
  );
}
